'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import Container from '@src/components/Shared/Container';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import NextImage from '@src/components/Shared/NextImage';
import SectionHeader from '@src/components/Shared/SectionHeader';
import ProductBox from '@src/components/Shared/ProductBox';
import InlineSVG from '@src/components/Shared/InlineSVG';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import SmartSpeedGrid from '@src/components/Sections/SmartSpeedGrid';
import CallToAction from '@src/components/Sections/CallToAction';
import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { SmartSpeedPageClientPropTypes } from './SmartSpeedPage.types';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';

const SmartSpeedPageClient: React.FC<SmartSpeedPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/products/smartspeed`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[0rem]! mb-[2rem]! md:mb-[0]! lg:mt-[9.4rem] 2xl:mb-[0]!'
        imgClassName='mt-[2rem] md:absolute md:right-[2rem] md:w-[35rem] lg:right-[5rem] lg:relative lg:w-[40rem] lg:left-[-2rem] 2xl:left-[-10rem] 2xl:w-3xl 3xl:w-[50rem] 3xl:left-[-20rem] xsm:min-h-[42.7984rem] md:min-h-[39.7570rem] lg:min-h-[45.4297rem] 2xl:min-h-[54.5156rem] 3xl:min-h-[56.7875 md:[&>img]:mt-[9.5rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:mt-[4.1rem] lg:max-w-[51.4rem] xsm:min-h-[5.1641rem] lg:min-h-[8.3719rem] 2xl:min-h-[9.6063rem]'
        introClassName='tracking-[-0.0342rem] mb-[1rem] leading-[2.7rem] text-[1.8rem] max-w-[38.4rem] md:text-[2rem] md:leading-[3rem] md:tracking-[-0.036rem] md:max-w-[38.6rem] lg:max-w-[48.5rem] lg:text-[2.4rem] lg:leading-[3.6rem] 2xl:max-w-[58.4rem]'
        headerClassName='xsm:mx-auto md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%] 2xl:w-[50%] 3xl:w-[65%]'

      />

      <ImageTextSecondary
        reversed
        background='lg:bg-background-tertiary'
        className='lg:overflow-visible! lg:py-[4rem]'
        id={page?.everyAspect?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.everyAspect?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.everyAspect?.items?.[0]?.mediaPrimary?.url}
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='  2xl:max-w-[78.2rem]'
              bodyCopy={page?.everyAspect?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons items={page?.everyAspect?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        id={page?.minusTheFrustration?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.minusTheFrustration?.items?.[0]?.preTitle}
        title={page?.minusTheFrustration?.items?.[0]?.title}
        imgURL={page?.minusTheFrustration?.items?.[0]?.mediaPrimary?.url}
        imgFirstClassName='mt-[-1rem] md:mt-[-2rem] lg:mt-[unset]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.minusTheFrustration?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.minusTheFrustration?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        background='lg:bg-background-tertiary'
        className='lg:overflow-visible! lg:pt-[4rem]'
        id={page?.allFromOneApp?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.allFromOneApp?.items?.[0]?.preTitle}
        title={page?.allFromOneApp?.items?.[0]?.title}
        titleClassName='2xl:max-w-[58.8rem]'
        imgURL={page?.allFromOneApp?.items?.[0]?.mediaPrimary?.url}
        imgSecondClassName='lg:max-w-[40rem]'
        imgAlt={page?.allFromOneApp?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.allFromOneApp?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.allFromOneApp?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        className='mb-[0]! md:flex md:flex-col'
        id={page?.wideRange?.items?.[0]?.scrollTo?.slice(1)}
      >
        <NextImage
          className='mb-[2.4rem] md:order-1 md:mx-auto md:mb-[unset] md:w-[62.2rem]'
          src={page?.wideRange?.items?.[0]?.mediaPrimary?.url}
          alt={page?.wideRange?.items?.[0]?.mediaPrimary?.description}
          aspectRatio={
            page?.wideRange?.items?.[0]?.mediaPrimary?.width /
            page?.wideRange?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.wideRange?.items?.[0]?.mediaPrimary?.width} // Set actual width
        />

        <SectionHeader
          className='justify-center pb-[3.3rem] md:mb-[3.4rem] lg:pb-[unset]'
          pretitle={page?.wideRange?.items?.[0]?.preTitle}
          title={page?.wideRange?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light 2xl:locale-es:max-w-[85.8rem] 2xl:max-w-[58.8rem] '
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.wideRange?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='justify-center'
              items={page?.wideRange?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <ImageTextSecondary
        reversed
        className='overflow-visible!'
        background='bg-background-tertiary'
        id={page?.upInLights?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.upInLights?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.upInLights?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.upInLights?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.upInLights?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Container
        id={page?.smartSpeedPlus?.items?.[0]?.scrollTo?.slice(1)}
        className='2x:mt-[-10rem] mb-[0]! flex flex-col items-center justify-center lg:flex-row 2xl:mb-[0]!'
      >
        <ProductBox className='lg:max-w-[55rem]' product={page?.smartSpeedPlus?.items?.[0]} />
        <NextImage
          className='w-full md:w-[50%] md:max-w-4xl '
          src={page?.smartSpeedPlus?.items?.[0]?.mediaPrimary?.url}
          alt={page?.smartSpeedPlus?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          objectFit='contain'
          aspectRatio={
            page?.smartSpeedPlus?.items?.[0]?.mediaPrimary?.width /
            page?.smartSpeedPlus?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.smartSpeedPlus?.items?.[0]?.mediaPrimary?.width} // Set actual width
        />
      </Container>
      <SmartSpeedGrid smartSpeed={page?.smartSpeedPlus?.items?.[0]?.extraAssetsCollection?.items} />

      <Container
        background='bg-background-tertiary'
        className='flex flex-col-reverse py-[2rem] xl:mb-[0]! xl:flex-row xl:gap-[2rem] 3xl:py-[5rem]!'
        id={page?.smartSpeedDash?.items?.[0]?.scrollTo?.slice(1)}
      >
        <InlineSVG
          className='mx-auto md:hidden'
          src={page?.smartSpeedDash?.items?.[0]?.mediaSecondary?.url}
          renderAt={['mobile']}
        />
        <InlineSVG
          className='mx-auto hidden md:block '
          src={page?.smartSpeedDash?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop', 'tablet']}
        />

        <ProductBox
          className='bg-tertiary! hidden p-[2rem] lg:block lg:max-w-[47rem]! xl:self-center'
          product={page?.smartSpeedDash?.items?.[0]}
        />

        <SectionHeader
          className='mt-[3rem] justify-center pb-[3.3rem] md:mb-[3.4rem] lg:hidden lg:pb-[unset] '
          pretitle={page?.smartSpeedDash?.items?.[0]?.preTitle}
          title={page?.smartSpeedDash?.items?.[0]?.title}
          titleClassName='text-[2.8rem] [&>span]:drop-shadow-xs xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='left'
        >
          <>
            <RenderBodyCopy
              className='text-tertiary md:max-w-[60rem] md:text-[1.8rem]'
              bodyCopy={page?.smartSpeedDash?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons
              className='justify-left'
              items={page?.smartSpeedDash?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <Container>
        <ProductBox
          className='mb-[5.2rem] mt-[8rem] md:max-w-[45rem] lg:max-w-[58.1rem]!'
          product={page?.smartSpeedRange?.items?.[0]}
        />

        <div className='md:flex md:flex-col'>
          <InlineSVG
            className='mb-[2.4rem] max-w-[24.7rem] md:mb-[0]  md:mt-auto md:hidden lg:mb-[-5rem]'
            src={page?.smartJump?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop', 'tablet']}
          />
          <div className='md:flex md:items-center md:justify-center'>
            <NextImage
              className='mb-[2.4rem] md:relative md:bottom-[-4rem] md:order-1 md:mb-[0] md:w-[50%] lg:bottom-[-2rem]'
              src={page?.smartJump?.items?.[0]?.mediaSecondary?.url}
              alt={page?.smartJump?.items?.[0]?.mediaSecondary?.description ?? 'VALD Image'}
              aspectRatio={
                page?.smartJump?.items?.[0]?.mediaSecondary?.width /
                page?.smartJump?.items?.[0]?.mediaSecondary?.height
              }
              width={page?.smartJump?.items?.[0]?.mediaSecondary?.width}
            />

            <div className='md:w-[50%]'>
              <InlineSVG
                className='mb-[2.4rem] hidden max-w-[24.7rem] md:block lg:w-[60%] lg:max-w-[30rem] 3xl:max-w-[35rem]'
                renderAt={['desktop', 'tablet']}
                src={page?.smartJump?.items?.[0]?.mediaPrimary?.url}
              />

              <RenderBodyCopy bodyCopy={page?.smartJump?.items?.[0]?.bodyCopy?.json?.content} />

              <RenderButtons items={page?.smartJump?.items?.[0]?.buttonCollection?.items} />
            </div>
          </div>
        </div>

        <hr className='my-[2.4rem] border-border md:mb-[-2.4rem] md:mt-[4.8rem] lg:mb-[2.4rem] lg:mt-[8rem]' />

        <Container id={page?.smartJump?.items?.[0]?.scrollTo?.slice(1)} className='px-[0]!'>
          <div className='grid grid-cols-1 gap-[1.6rem] md:grid-cols-2 lg:grid-cols-3 '>
            {page?.smartJump?.items?.[0]?.extraAssetsCollection?.items?.map((item) => {
              return (
                <div
                  key={`${item?.sys?.id}-smart-jump-accessories`}
                  className='rounded-3xl  bg-background-tertiary px-[2rem] pb-[2rem]'
                >
                  <NextImage
                    className='max-w-[45rem] md:mb-[2.4rem] lg:mx-auto 3xl:max-h-[30rem]'
                    src={item?.mediaPrimary?.url}
                    alt={item?.mediaPrimary?.description ?? 'smartjump accesories'}
                    aspectRatio={item?.mediaPrimary?.width / item?.mediaPrimary?.height}
                    width={item?.mediaPrimary?.width}
                  />
                  <h3
                    style={{
                      textShadow:
                        'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
                    }}
                    className='mb-[1.8rem] text-[2.2rem] font-normal leading-none text-primary drop-shadow-xs md:text-[2.2rem] 2xl:text-[3.2rem] '
                  >
                    {item?.title}
                  </h3>
                  <RenderBodyCopy bodyCopy={item.bodyCopy?.json?.content} />
                </div>
              );
            })}
          </div>
        </Container>

        <Container
          id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
          background='bg-[#F9F9F9]'
          className='mt-[0]! py-[8rem]'
        >
          <SectionHeader
            className='mb-[2.4rem] lg:mb-[unset]'
            childrenClassName='w-full'
            pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
            title={page?.valdSubscription?.items?.[0]?.title}
            titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
            align='center'
          >
            <RenderButtons
              items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
              className='mt-[1rem]! justify-center'
            />
          </SectionHeader>
          <SubscriptionTable
            headerImgL='w-[11rem] lg:w-[13rem] lg:mt-[-3.35rem]'
            headerImgR='w-[8rem] lg:w-[11rem] lg:mt-[-5rem]'
            data={page?.valdSubscription?.items}
          />
        </Container>

        <CallToAction
          attr='call-to-action-component'
          id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
          title={page?.callToAction?.items?.[0]?.title}
          bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
          buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
        />
      </Container>
    </>
  );
};

export default SmartSpeedPageClient;
